import { useAutoAnimate } from "@formkit/auto-animate/react";
import { SlidersHorizontal, X } from "lucide-react";
import { Children, useId, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export type FilterChip = { id: string; label: ReactNode; onRemove: () => void };

/** How many quick controls sit next to the search box. More than this is a design smell: move them to `advanced`. */
export const MAX_QUICK_FILTERS = 3;

/**
 * The one filter pattern for lists: search + up to 3 quick controls + a "Filters" toggle that opens the
 * advanced controls in a collapsible row, then the active-filter chips with "Clear filters".
 * At most 5 controls are visible at once (search, ≤3 quick, Filters); everything else waits behind "Filters".
 *
 * Keep the state in the URL with `useUrlFilters` so links, reloads and the back button keep the view.
 */
export function FilterBar({
  search,
  quick,
  advanced,
  advancedCount = 0,
  defaultAdvancedOpen = false,
  chips = [],
  onClear,
  end,
  className,
  label,
}: {
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  /** Quick controls (Select, Segmented…). Up to 3 are shown; extra ones are moved into the advanced row. */
  quick?: ReactNode;
  /** Controls shown only after "Filters" is opened. */
  advanced?: ReactNode;
  /** How many advanced filters are set (shown on the toggle). */
  advancedCount?: number;
  defaultAdvancedOpen?: boolean;
  chips?: FilterChip[];
  /** Resets every filter. Shown when any chip is active. */
  onClear?: () => void;
  /** Trailing controls that are not filters (density toggle, view switch). */
  end?: ReactNode;
  className?: string;
  /** Accessible name of the toolbar. */
  label?: string;
}) {
  const { t } = useT();
  const id = useId();
  const [open, setOpen] = useState(defaultAdvancedOpen);
  const [chipsRef] = useAutoAnimate<HTMLDivElement>({ duration: 160 });
  const quickList = Children.toArray(quick);
  const shown = quickList.slice(0, MAX_QUICK_FILTERS);
  const overflow = quickList.slice(MAX_QUICK_FILTERS);
  const hasAdvanced = !!advanced || overflow.length > 0;
  return (
    <div className={cn("mb-4", className)} data-testid="filter-bar">
      <div role="toolbar" aria-label={label ?? t("kit.filters")} className="flex flex-wrap items-center gap-2">
        {search && (
          <SearchInput
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder ?? t("kit.search")}
            inputSize="sm"
            className="w-full min-w-0 sm:w-64"
          />
        )}
        {shown}
        {hasAdvanced && (
          <Button
            variant={open ? "secondary" : "ghost"}
            size="sm"
            icon={<SlidersHorizontal />}
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen((o) => !o)}
            data-testid="filters-toggle"
          >
            {t("kit.filters")}
            {advancedCount > 0 && <span className="num grid h-5 min-w-5 place-items-center rounded-full bg-accent-soft px-1 text-[11px] font-semibold text-accent-ink">{advancedCount}</span>}
          </Button>
        )}
        {end && <div className="ms-auto flex items-center gap-2">{end}</div>}
      </div>
      {hasAdvanced && (
        <div id={id} role="region" aria-label={t("kit.filters")} hidden={!open} className="mt-2 rounded-2xl border border-line bg-surface-2/50 p-3">
          {open && (
            <div className="flex flex-wrap items-center gap-2">
              {overflow}
              {advanced}
            </div>
          )}
        </div>
      )}
      <div ref={chipsRef} role="group" aria-label={t("kit.activeFilters")} className={cn("flex flex-wrap items-center gap-1.5", chips.length > 0 && "mt-2")}>
        {chips.map((c) => (
          <span key={c.id} className="inline-flex h-7 items-center gap-1 rounded-lg bg-accent-soft ps-2.5 pe-1 text-[12.5px] font-medium text-accent-ink">
            {c.label}
            <button type="button" onClick={c.onRemove} aria-label={t("kit.removeFilter", { label: typeof c.label === "string" ? c.label : c.id })} className="grid size-5 place-items-center rounded-md hover:bg-[color-mix(in_srgb,var(--accent)_14%,transparent)]">
              <X className="size-3.5" aria-hidden />
            </button>
          </span>
        ))}
        {chips.length > 0 && onClear && (
          <Button variant="link" size="xs" onClick={onClear} className="ms-1" data-testid="clear-filters">
            {t("kit.clearFilters")}
          </Button>
        )}
      </div>
    </div>
  );
}
